export type JourneyProgress = {
  appearanceCount: number;
  status: 'active' | 'completed' | 'suppressed';
  version: number;
};

type JourneyStorage = Pick<Storage, 'getItem' | 'setItem'>;

export const createJourneyProgress = (version: number): JourneyProgress => ({
  appearanceCount: 0,
  status: 'active',
  version,
});

export const canAutoShowJourney = (
  progress: JourneyProgress,
  maximumAppearances: number,
): boolean => progress.status === 'active' && progress.appearanceCount < maximumAppearances;

export const registerJourneyAppearance = (progress: JourneyProgress): JourneyProgress => ({
  ...progress,
  appearanceCount: progress.appearanceCount + 1,
});

export const getJourneyStorageKey = (
  namespace: string,
  journeyId: string,
  version: number,
): string => `knowme:guided-journey:${namespace}:${journeyId}:v${version}`;

export const suppressJourneyProgress = (progress: JourneyProgress): JourneyProgress => ({
  ...progress,
  status: 'suppressed',
});

export const completeJourneyProgress = (progress: JourneyProgress): JourneyProgress => ({
  ...progress,
  status: 'completed',
});

const isJourneyProgress = (value: unknown): value is JourneyProgress => {
  if (!value || typeof value !== 'object') return false;

  const candidate = value as Partial<JourneyProgress>;
  return (
    typeof candidate.appearanceCount === 'number' &&
    ['active', 'completed', 'suppressed'].includes(candidate.status ?? '') &&
    typeof candidate.version === 'number'
  );
};

export const readJourneyProgress = (
  storage: Pick<JourneyStorage, 'getItem'>,
  key: string,
  version: number,
): JourneyProgress => {
  try {
    const storedValue = storage.getItem(key);
    if (!storedValue) return createJourneyProgress(version);

    const parsed: unknown = JSON.parse(storedValue);
    if (!isJourneyProgress(parsed) || parsed.version !== version) {
      return createJourneyProgress(version);
    }

    return parsed;
  } catch {
    return createJourneyProgress(version);
  }
};

export const saveJourneyProgress = (
  storage: Pick<JourneyStorage, 'setItem'>,
  key: string,
  progress: JourneyProgress,
): void => {
  try {
    storage.setItem(key, JSON.stringify(progress));
  } catch {
    return;
  }
};
